import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { AnimalFilterTab } from "@/components/filter-tab/animal-filter-tab/animal-filter-tab.tsx"
import { LoadingAnimation } from "@/components/loading-animation/loading-animation.tsx"
import NoResultsFound from "@/components/not-found/no-results-found.tsx"
import PageContentWrapperSmall from "@/components/page-content-wrappers/page-content-wrapper-small.tsx"
import DesktopTopBar from "@/components/top-bars/desktop-top-bar.tsx"
import MobileTopBar from "@/components/top-bars/mobile-top-bar/mobile-top-bar.tsx"
import AnimalCard from "@/features/farm/components/animal-card/animal-card.tsx"
import AnimalCardWrapper from "@/features/farm/components/animal-card-wrapper/animal-card-wrapper.tsx"
import AnimalTable from "@/features/farm/components/animal-table/animal-table.tsx"
import CountNumber from "@/features/farm/components/count-number/count-number.tsx"
import useAnimalFilterURL from "@/features/farm/hooks/use-animal-filter-URL.ts"
import {
  ANIMAL_PROFILE_VIEW,
  NEW_ANIMAL_VIEW,
} from "@/features/farm/routes/route-list.ts"
import { HOME_SCREEN } from "@/features/home/routes/route-list.ts"
import { useAppSelector } from "@/redux/hooks.ts"

export const FarmTab: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    selections,
    animals,
    isLoading,
    handleAgeSelection,
    handleGenderSelection,
    handleSpeciesSelection,
    handleSearchQuery,
    handleStatusSelection,
    handleOutOfExploitationSelection,
  } = useAnimalFilterURL()

  const animalDrafts = useAppSelector((state) => state.animalDraftSlice.drafts)

  const filteredAnimals = useMemo(() => {
    return animals.map((animal) => {
      const draft = animalDrafts[animal.id]
      return draft ? { ...animal, hasChanges: true } : animal
    })
  }, [animals, animalDrafts])

  const handleAnimalCardClick = (animalId: string) => {
    navigate(`${ANIMAL_PROFILE_VIEW}/${animalId}`)
  }

  return (
    <>
      <MobileTopBar
        title={t("myFarm")}
        navigateTo={NEW_ANIMAL_VIEW}
        onBackClick={() => navigate(HOME_SCREEN)}
      />

      <PageContentWrapperSmall>
        <DesktopTopBar
          title={t("myFarm")}
          navigateTo={NEW_ANIMAL_VIEW}
          onBackClick={() => navigate(HOME_SCREEN)}
        />
        <AnimalFilterTab
          selections={selections}
          handleSearch={handleSearchQuery}
          handleAgeChange={handleAgeSelection}
          handleGenderChange={handleGenderSelection}
          handleSpeciesChange={handleSpeciesSelection}
          handleStatusChange={handleStatusSelection}
          handleOutOfExploitationChange={handleOutOfExploitationSelection}
        />

        <CountNumber count={filteredAnimals.length} text={t("heads")} />

        {isLoading ? (
          <div className="flex h-[calc(100vh-400px)] items-center justify-center">
            <LoadingAnimation />
          </div>
        ) : filteredAnimals.length === 0 ? (
          <div className="mt-[100px] flex w-full justify-center">
            <NoResultsFound message={t("noAnimalsFoundForFilter")} />
          </div>
        ) : (
          <>
            {/* Mobile: Cards */}
            <div className="lg:hidden">
              {filteredAnimals.map((animal) => (
                <AnimalCardWrapper
                  key={animal.id}
                  onClick={() => handleAnimalCardClick(animal.id)}
                >
                  <AnimalCard animal={animal} />
                </AnimalCardWrapper>
              ))}
            </div>

            {/* Desktop: Table */}
            <div className="hidden lg:block">
              <AnimalTable
                animals={filteredAnimals}
                onRowClick={handleAnimalCardClick}
              />
            </div>
          </>
        )}
      </PageContentWrapperSmall>
    </>
  )
}
